import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageWrapper } from "@/components/PageWrapper";
import { StickyNotification } from "@/components/StickyNotification";
import { WhatsAppChat } from "@/components/WhatsAppChat";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import Index from "./pages/Index";

// Lazy load route components for code splitting
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const SmartPricing = lazy(() => import("./pages/SmartPricing"));
const Consulting = lazy(() => import("./pages/Consulting"));
const Research = lazy(() => import("./pages/Research"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const DataProcessing = lazy(() => import("./pages/DataProcessing"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Routes>
                {/* Admin routes */}
                <Route path="/admin/login" element={
                  <Suspense fallback={<div className="min-h-screen bg-background" />}>
                    <AdminLogin />
                  </Suspense>
                } />
                <Route path="/admin/*" element={
                  <Suspense fallback={<div className="min-h-screen bg-background" />}>
                    <Admin />
                  </Suspense>
                } />
                
                {/* Main app routes */}
                <Route path="*" element={
                  <div className="min-h-screen bg-background flex flex-col">
                    <Header />
                    <main className="flex-1">
                      <PageWrapper>
                        <Suspense fallback={<div className="min-h-[50vh]" />}>
                          <Routes>
                            {/* English routes */}
                            <Route path="/" element={<Index />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/smart-pricing" element={<SmartPricing />} />
                            <Route path="/consulting" element={<Consulting />} />
                            <Route path="/research" element={<Research />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="/terms-of-service" element={<TermsOfService />} />
                            <Route path="/cookie-policy" element={<CookiePolicy />} />
                            <Route path="/disclaimer" element={<Disclaimer />} />
                            <Route path="/data-processing" element={<DataProcessing />} />
                            
                            {/* Spanish routes */}
                            <Route path="/es" element={<Index />} />
                            <Route path="/es/" element={<Index />} />
                            <Route path="/es/acerca" element={<About />} />
                            <Route path="/es/servicios" element={<Services />} />
                            <Route path="/es/smart-pricing" element={<SmartPricing />} />
                            <Route path="/es/consultoria" element={<Consulting />} />
                            <Route path="/es/investigacion" element={<Research />} />
                            <Route path="/es/blog" element={<Blog />} />
                            <Route path="/es/blog/:slug" element={<BlogPost />} />
                            <Route path="/es/contacto" element={<Contact />} />
                            <Route path="/es/politica-privacidad" element={<PrivacyPolicy />} />
                            <Route path="/es/terminos-servicio" element={<TermsOfService />} />
                            <Route path="/es/politica-cookies" element={<CookiePolicy />} />
                            <Route path="/es/descargo" element={<Disclaimer />} />
                            <Route path="/es/procesamiento-datos" element={<DataProcessing />} />
                            
                            {/* Legacy routes - redirect handled by component */}
                            <Route path="/services/smart-pricezing" element={<SmartPricing />} />
                            <Route path="/services/pricing-strategy-consulting" element={<Consulting />} />
                            <Route path="/services/international-price-research" element={<Research />} />
                            
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </PageWrapper>
                    </main>
                    <Footer />
                    <StickyNotification />
                    <WhatsAppChat />
                    <CookieConsentBanner />
                  </div>
                } />
              </Routes>
            </TooltipProvider>
          </LanguageProvider>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
